import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';
import { logout } from '../../../redux/features/auth.features';

const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [loginTime, setLoginTime] = useState(null);
    const [isExpiringSoon, setIsExpiringSoon] = useState(false);
    const dispatch = useDispatch();

    const calculateTimeLeft = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) return null;

        try {
            const decoded = jwtDecode(token);
            if (!loginTime && decoded.iat) {
                setLoginTime(decoded.iat);
            }
            const currentTime = Math.floor(Date.now() / 1000);
            const remaining = decoded.exp - currentTime;
            return remaining > 0 ? remaining : 0;
        } catch (error) {
            console.error("Session decode failed:", error);
            return 0;
        }
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            const remaining = calculateTimeLeft();
            setTimeLeft(remaining);

            if (remaining !== null) {
                // 5 minutes = 300 seconds
                if (remaining <= 300 && remaining > 0) {
                    setIsExpiringSoon(true);
                } else {
                    setIsExpiringSoon(false);
                }

                if (remaining <= 0) {
                    dispatch(logout());
                    window.location.href = '/login';
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [calculateTimeLeft, dispatch]);

    const formatTime = (seconds) => {
        if (seconds === null) return "--:--";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatEasyTime = (seconds) => {
        if (seconds === null) return "Unknown";
        if (seconds < 0) return "0s";
        if (seconds < 60) return `${seconds}s`;

        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);

        if (hrs > 0) {
            return `${hrs}h ${mins}m`;
        }
        return `${mins}m ${seconds % 60}s`;
    };

    const getActiveDuration = () => {
        if (!loginTime) return null;
        const currentTime = Math.floor(Date.now() / 1000);
        return currentTime - loginTime;
    };

    return (
        <SessionContext.Provider value={{ timeLeft, isExpiringSoon, formatTime, formatEasyTime, getActiveDuration }}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};
